import {IPropertyDetail} from "../../property/types/IPropertyDetail";
import {IRegionMonthlyAveragePrices} from "../../region/api/fetch_regions_monthly_average_prices";
import {Country} from "../../region/types/Country";
import {ISearchInputRegion} from "../../search/components/ISearchInputValue";
import {SearchTab} from "../../search/types/SearchTab";
import {IRecentFilter} from "../../search/utils/recent_filters_storage";
import {IPersonalizedLocationPrice} from "../types/IPersonalizedHomepage";

const PERSONALIZED_LOCATION_LIMIT = 20;
const PERSONALIZED_PRICE_HISTORY_LIMIT = 6;
/** A trend needs at least a start and an end price. */
const PERSONALIZED_PRICE_HISTORY_MIN_POINTS = 2;

export interface IPersonalizedPriceRegion {
    /** Stable card id, unique per requested region. */
    id: string;
    regionId: number;
    locationName: string;
}

export const getPersonalizedPriceRegions = (
    recentFilters: IRecentFilter[],
    recentlyViewedPropertyDetails: IPropertyDetail[] = []
): IPersonalizedPriceRegion[] => {
    const searchRegions = recentFilters.flatMap((recentFilter) => {
        if (recentFilter.search?.tabType !== SearchTab.Regions) {
            return [];
        }

        // Stored filters come from localStorage — legacy or corrupted entries may
        // miss the regions array entirely and must not crash the homepage.
        return Array.isArray(recentFilter.search.regions) ? recentFilter.search.regions : [];
    });

    const searchPriceRegions = searchRegions
        .filter((region) => Boolean(region) && typeof region.id === "number" && Boolean(region.full_name_reverted || region.name))
        .filter((region) => region.country == null || region.country === Country.POLAND)
        .map(mapSearchRegionToPriceRegion);
    const viewedPriceRegions = recentlyViewedPropertyDetails
        .filter((property) => property.offer.region.country === Country.POLAND)
        .map(mapPropertyToPriceRegion);

    return [...searchPriceRegions, ...viewedPriceRegions]
        .filter((priceRegion, index, allPriceRegions) => allPriceRegions.findIndex((candidate) => candidate.regionId === priceRegion.regionId) === index)
        .slice(0, PERSONALIZED_LOCATION_LIMIT);
};

/**
 * Turns the `average_price_m2_by_month` series into a card row. Returns
 * null when the region has no usable data (empty object = no BigData coverage,
 * or fewer than two priced months) — such a region is dropped from the list.
 */
export const mapMonthlyAveragePricesToPersonalized = (params: {
    priceRegion: IPersonalizedPriceRegion;
    monthlyAveragePrices: IRegionMonthlyAveragePrices | undefined;
}): IPersonalizedLocationPrice | null => {
    const fullHistory = getMonthlyPriceHistory(params.monthlyAveragePrices);
    const historyEntries = fullHistory.slice(-PERSONALIZED_PRICE_HISTORY_LIMIT);
    const history = historyEntries.map(([, price]) => price);

    if (history.length < PERSONALIZED_PRICE_HISTORY_MIN_POINTS) {
        return null;
    }

    const historyPrecedingPrice = fullHistory.length > history.length ? fullHistory[fullHistory.length - history.length - 1][1] : null;

    const firstPrice = history[0];
    const lastPrice = history[history.length - 1];

    return {
        id: params.priceRegion.id,
        locationName: params.priceRegion.locationName,
        pricePerSquareMeter: Math.round(lastPrice),
        changePercent: Number((((lastPrice - firstPrice) / firstPrice) * 100).toFixed(1)),
        history,
        historyMonths: historyEntries.map(([month]) => month),
        historyPrecedingPrice,
        source: "api"
    };
};

/** The latest `YYYY-MM` month that carries data across the given series, or null. */
export const getLatestPricedMonth = (allMonthlyAveragePrices: (IRegionMonthlyAveragePrices | undefined)[]): string | null => {
    const pricedMonths = allMonthlyAveragePrices.flatMap((monthlyAveragePrices) =>
        Object.entries(monthlyAveragePrices ?? {})
            .filter(([, price]) => typeof price === "number" && Number.isFinite(price) && price > 0)
            .map(([month]) => month)
    );

    return pricedMonths.length > 0 ? pricedMonths.sort()[pricedMonths.length - 1] : null;
};

/** Equal-length `YYYY-MM` keys sort lexicographically into chronological order. */
const getMonthlyPriceHistory = (monthlyAveragePrices: IRegionMonthlyAveragePrices | undefined): [string, number][] =>
    Object.entries(monthlyAveragePrices ?? {})
        .sort(([leftMonth], [rightMonth]) => leftMonth.localeCompare(rightMonth))
        .filter((entry): entry is [string, number] => typeof entry[1] === "number" && Number.isFinite(entry[1]) && entry[1] > 0);

const mapSearchRegionToPriceRegion = (region: ISearchInputRegion): IPersonalizedPriceRegion => ({
    id: `location-price-${region.id}`,
    regionId: region.id,
    locationName: region.full_name_reverted || region.name
});

const mapPropertyToPriceRegion = (property: IPropertyDetail): IPersonalizedPriceRegion => {
    const offerRegion = property.offer.region;
    // Query the district (or city) rather than the offer's own region, so two
    // estates in one district collapse into a single row. Scenario-driven
    // payloads can be thinner than the type promises, so both the region id and
    // the short "City, District" label degrade gracefully.
    const district = offerRegion.stats?.region_type_district;
    const city = offerRegion.stats?.region_type_city;
    const regionId = district?.id ?? city?.id ?? offerRegion.id;
    const shortLocationName = [city?.name, district?.name].filter(Boolean).join(", ");

    return {
        id: `location-price-${regionId}`,
        regionId,
        locationName: shortLocationName || offerRegion.full_name_reverted
    };
};
import {IPropertyDetail} from "../../property/types/IPropertyDetail";
import {IRegionMonthlyAveragePrices} from "../../region/api/fetch_regions_monthly_average_prices";
import {Country} from "../../region/types/Country";
import {ISearchInputRegion} from "../../search/components/ISearchInputValue";
import {SearchTab} from "../../search/types/SearchTab";
import {IRecentFilter} from "../../search/utils/recent_filters_storage";
import {IPersonalizedLocationPrice} from "../types/IPersonalizedHomepage";

const PERSONALIZED_LOCATION_LIMIT = 20;
const PERSONALIZED_PRICE_HISTORY_LIMIT = 6;
/** A trend needs at least a start and an end price. */
const PERSONALIZED_PRICE_HISTORY_MIN_POINTS = 2;

export interface IPersonalizedPriceRegion {
    /** Stable card id, unique per requested region. */
    id: string;
    regionId: number;
    locationName: string;
}

export const getPersonalizedPriceRegions = (
    recentFilters: IRecentFilter[],
    recentlyViewedPropertyDetails: IPropertyDetail[] = []
): IPersonalizedPriceRegion[] => {
    const searchRegions = recentFilters.flatMap((recentFilter) => {
        if (recentFilter.search?.tabType !== SearchTab.Regions) {
            return [];
        }

        // Stored filters come from localStorage — legacy or corrupted entries may
        // miss the regions array entirely and must not crash the homepage.
        return Array.isArray(recentFilter.search.regions) ? recentFilter.search.regions : [];
    });

    const searchPriceRegions = searchRegions
        .filter((region) => Boolean(region) && typeof region.id === "number" && Boolean(region.full_name_reverted || region.name))
        .filter((region) => region.country == null || region.country === Country.POLAND)
        .map(mapSearchRegionToPriceRegion);
    const viewedPriceRegions = recentlyViewedPropertyDetails
        .filter((property) => property.offer.region.country === Country.POLAND)
        .map(mapPropertyToPriceRegion);

    return [...searchPriceRegions, ...viewedPriceRegions]
        .filter((priceRegion, index, allPriceRegions) => allPriceRegions.findIndex((candidate) => candidate.regionId === priceRegion.regionId) === index)
        .slice(0, PERSONALIZED_LOCATION_LIMIT);
};

/**
 * Turns the `average_price_m2_by_month` series into a card row. Returns
 * null when the region has no usable data (empty object = no BigData coverage,
 * or fewer than two priced months) — such a region is dropped from the list.
 */
export const mapMonthlyAveragePricesToPersonalized = (params: {
    priceRegion: IPersonalizedPriceRegion;
    monthlyAveragePrices: IRegionMonthlyAveragePrices | undefined;
}): IPersonalizedLocationPrice | null => {
    const fullHistory = getMonthlyPriceHistory(params.monthlyAveragePrices);
    const historyEntries = fullHistory.slice(-PERSONALIZED_PRICE_HISTORY_LIMIT);
    const history = historyEntries.map(([, price]) => price);

    if (history.length < PERSONALIZED_PRICE_HISTORY_MIN_POINTS) {
        return null;
    }

    const historyPrecedingPrice = fullHistory.length > history.length ? fullHistory[fullHistory.length - history.length - 1][1] : null;

    const firstPrice = history[0];
    const lastPrice = history[history.length - 1];

    return {
        id: params.priceRegion.id,
        locationName: params.priceRegion.locationName,
        pricePerSquareMeter: Math.round(lastPrice),
        changePercent: Number((((lastPrice - firstPrice) / firstPrice) * 100).toFixed(1)),
        history,
        historyMonths: historyEntries.map(([month]) => month),
        historyPrecedingPrice,
        source: "api"
    };
};

/** The latest `YYYY-MM` month that carries data across the given series, or null. */
export const getLatestPricedMonth = (allMonthlyAveragePrices: (IRegionMonthlyAveragePrices | undefined)[]): string | null => {
    const pricedMonths = allMonthlyAveragePrices.flatMap((monthlyAveragePrices) =>
        Object.entries(monthlyAveragePrices ?? {})
            .filter(([, price]) => typeof price === "number" && Number.isFinite(price) && price > 0)
            .map(([month]) => month)
    );

    return pricedMonths.length > 0 ? pricedMonths.sort()[pricedMonths.length - 1] : null;
};

/** Equal-length `YYYY-MM` keys sort lexicographically into chronological order. */
const getMonthlyPriceHistory = (monthlyAveragePrices: IRegionMonthlyAveragePrices | undefined): [string, number][] =>
    Object.entries(monthlyAveragePrices ?? {})
        .sort(([leftMonth], [rightMonth]) => leftMonth.localeCompare(rightMonth))
        .filter((entry): entry is [string, number] => typeof entry[1] === "number" && Number.isFinite(entry[1]) && entry[1] > 0);

const mapSearchRegionToPriceRegion = (region: ISearchInputRegion): IPersonalizedPriceRegion => ({
    id: `location-price-${region.id}`,
    regionId: region.id,
    locationName: region.full_name_reverted || region.name
});

const mapPropertyToPriceRegion = (property: IPropertyDetail): IPersonalizedPriceRegion => {
    const offerRegion = property.offer.region;
    // Query the district (or city) rather than the offer's own region, so two
    // estates in one district collapse into a single row. Scenario-driven
    // payloads can be thinner than the type promises, so both the region id and
    // the short "City, District" label degrade gracefully.
    const district = offerRegion.stats?.region_type_district;
    const city = offerRegion.stats?.region_type_city;
    const regionId = district?.id ?? city?.id ?? offerRegion.id;
    const shortLocationName = [city?.name, district?.name].filter(Boolean).join(", ");

    return {
        id: `location-price-${regionId}`,
        regionId,
        locationName: shortLocationName || offerRegion.full_name_reverted
    };
};
